import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Folder, Clock, Copy, Check } from 'lucide-react';
import TextAreaField from '../../Forms/Input/TextAreaField';

interface SignatureResultBoxProps {
  signature: string;
  fileName: string;
  signedAt: string;
}

const SignatureResultBox: React.FC<SignatureResultBoxProps> = ({
  signature,
  fileName,
  signedAt,
}) => {
  const [copied, setCopied] = useState(false);

  // 🕓 Formatear la fecha/hora de forma legible
  const formattedDate = new Date(signedAt).toLocaleString('es-SV', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(signature);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar la firma:', err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-8 w-full rounded-xl border border-[#3CA745]/60 bg-[#E9F7EE] p-6 shadow-md dark:border-[#3CA745]/40 dark:bg-[#1A2E1F]"
    >
      {/* 🔹 Encabezado principal */}
      <div className="mb-5 flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-[#3CA745]" />
        <h3 className="text-lg font-semibold text-[#3CA745] dark:text-[#3CA745]/90">
          Resultado de la Firma
        </h3>
      </div>

      {/* 🔹 Contenido */}
      <div className="text-gray-800 dark:text-gray-200 space-y-4 text-sm">
        {/* 🔸 Firma + botón copiar (en encabezado del campo) */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-gray-800 dark:text-gray-200 text-sm font-semibold">
              Firma generada (Base64)
            </label>

            <motion.button
              onClick={handleCopy}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-1 rounded-md bg-[#3CA745] px-2.5 py-1 text-xs font-semibold text-white shadow transition hover:bg-[#2E8536]"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copiar
                </>
              )}
            </motion.button>
          </div>

          <TextAreaField
            name="signature"
            value={signature}
            onChange={() => {}}
            rows={5}
            disabled
            iconKey="lock"
          />
        </div>

        {/* 🔸 Archivo */}
        <div className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Folder className="h-4 w-4 text-[#F9C107]" />
          <p className="text-sm">
            <b className="text-[#212B36] dark:text-white">Archivo:</b>{' '}
            {fileName}
          </p>
        </div>

        {/* 🔸 Fecha */}
        <div className="text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#3BA2B8]" />
          <p className="text-sm">
            <b className="text-[#212B36] dark:text-white">Firmado en:</b>{' '}
            {formattedDate}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default SignatureResultBox;
